import { useEffect, useState } from "react";
import wooRestApiService from "@/api/services/wooRestApiService";
import useAppDispatch from "@/hooks/useAppDispatch";
import { fetchAllWooData } from "@/helpers/helpers";
import { handleApiError } from "@/helpers/apiErrorHelper";
import { ProductType, type Product } from "@/types/products";
import type { ProductVariation } from "@/types/productVariations";

/**
 * fetch 有庫存資訊的變體資料
 * 職責：負責從 API 獲取變體資料，處理競態條件和錯誤處理
 */
interface UseStockedVariationsReturn {
  stockedVariations: ProductVariation[] | null;
  isVariationsLoading: boolean;
}

const useStockedVariationsData = (product: Product | null): UseStockedVariationsReturn => {
  const appDispatch = useAppDispatch();
  const [stockedVariations, setStockedVariations] = useState<ProductVariation[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchStockedVariations = async (productId: string, signal?: AbortSignal) => {
    if (!productId) return;
    setIsLoading(true);
    try {
      const allVariations = await fetchAllWooData<ProductVariation>(
        wooRestApiService.productVariations.get,
        productId,
        undefined,
        signal,
      );

      if (!signal?.aborted) {
        setStockedVariations(allVariations);
      }
    } catch (error) {
      if (signal?.aborted) return;
      handleApiError(error, appDispatch, "載入商品規格失敗，請稍後再試");
    } finally {
      if (signal?.aborted) return;
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!product || product.type !== ProductType.Variable) return;

    const controller = new AbortController();
    fetchStockedVariations(String(product.id), controller.signal);

    return () => {
      controller.abort();
    };
  }, [product]);

  // 當 product 切換時，立即重置狀態
  useEffect(() => {
    setStockedVariations(null);
  }, [product?.id]);

  return { stockedVariations, isVariationsLoading: isLoading };
};

export default useStockedVariationsData;
